import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { OpenCard } from "@liga/shared";
import { api } from "../api/client";
import { trackEvent } from "../analytics";
import { ErrorBox, Loading } from "../components/Loading";

type YearSelection = number | "all";

function formatEventDate(iso: string): string {
  // "2026-05-28" -> "28/05/2026"
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

/**
 * Public gallery of every event tagged with `is_open = 1`. Card layout
 * mirrors `/metagame` (aspect 16/9 banner + title + meta) so the two
 * feel like sibling views. The banner is the winning subarchetype's
 * representative card art crop (falls back to a striped placeholder
 * when no image is cached).
 */
export function OpensPage() {
  const yearsQuery = useQuery({
    queryKey: ["opensYears"],
    queryFn: api.opensYears,
  });

  const [selection, setSelection] = useState<YearSelection>("all");

  // As soon as we know the year list, pin the filter to the most recent
  // year — mirrors `MetagamePage`'s "latest year on load" ergonomics.
  useEffect(() => {
    if (selection !== "all") return;
    const years = yearsQuery.data;
    if (years && years.length > 0) {
      setSelection(years[0]!);
    }
  }, [yearsQuery.data, selection]);

  const opensQuery = useQuery({
    queryKey: ["opens", selection],
    queryFn: () =>
      selection === "all"
        ? api.opens({})
        : api.opens({ year: selection }),
  });

  const years = yearsQuery.data ?? [];
  const data = opensQuery.data;

  const summary = useMemo(() => {
    if (!data) return null;
    if (selection === "all") return "Todos os anos";
    return `Ano ${selection}`;
  }, [data, selection]);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#8a8170] font-bold mb-1.5">
            Torneios
          </div>
          <h1 className="font-display text-[44px] leading-none font-bold tracking-tight text-ink">
            Opens
          </h1>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 bg-white border border-line rounded-xl p-1.5">
          <button
            type="button"
            onClick={() => {
              setSelection("all");
              trackEvent("opens_year_change", { kind: "all" });
            }}
            className={[
              "px-4 py-2 rounded-lg text-sm font-semibold transition-colors",
              selection === "all"
                ? "bg-brand-700 text-white"
                : "text-[#6b6356] hover:bg-[#f3efe6]",
            ].join(" ")}
          >
            Todos
          </button>
          {years.length > 0 && (
            <span className="w-px h-6 bg-line mx-1" aria-hidden="true" />
          )}
          {years.map((y) => {
            const active = selection === y;
            return (
              <button
                key={y}
                type="button"
                onClick={() => {
                  setSelection(y);
                  trackEvent("opens_year_change", { kind: "year", value: y });
                }}
                className={[
                  "px-4 py-2 rounded-lg text-sm font-semibold transition-colors",
                  active
                    ? "bg-brand-700 text-white"
                    : "text-[#6b6356] hover:bg-[#f3efe6]",
                ].join(" ")}
              >
                {y}
              </button>
            );
          })}
        </div>
      </div>

      <p className="max-w-[640px] text-[15px] leading-relaxed text-[#6b6356]">
        Todos os Opens marcados pela organização. A imagem de cada card é a
        arte da carta representativa do subarquétipo do deck campeão.
      </p>

      {opensQuery.isLoading && <Loading />}
      {opensQuery.error && <ErrorBox error={opensQuery.error} />}

      {data && (
        <>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-[13px] text-[#8a8170] font-semibold">
            {summary && <span>{summary}</span>}
            <span>
              {data.opens.length} open{data.opens.length === 1 ? "" : "s"}
            </span>
          </div>

          {data.opens.length === 0 ? (
            <p className="text-slate-500">
              Nenhum Open registrado nesse período.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {data.opens.map((open) => (
                <OpenGalleryCard key={open.eventId} open={open} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function OpenGalleryCard({ open }: { open: OpenCard }) {
  const banner = open.bannerImageUrl;
  return (
    <article className="flex flex-col bg-white border border-line rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <Link
        to={`/events/${open.eventId}`}
        className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-700/60"
        aria-label={`Ver detalhes de ${open.eventName}`}
      >
        <div className="aspect-[16/9] bg-[#efece6] overflow-hidden relative">
          {banner ? (
            <img
              src={banner}
              alt=""
              loading="lazy"
              decoding="async"
              className="block w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-[repeating-linear-gradient(45deg,#efece6_0_11px,#f3efe6_11px_22px)]">
              <span className="font-mono text-[11px] text-[#9a9182] uppercase tracking-wide">
                sem arte
              </span>
            </div>
          )}
          <span className="absolute top-2 left-2 inline-block rounded-full bg-brand-700 text-white text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 shadow">
            Open
          </span>
        </div>
      </Link>

      <div className="p-4 flex flex-col gap-2.5 min-w-0">
        <div className="min-w-0">
          <Link
            to={`/events/${open.eventId}`}
            className="font-display font-bold text-[16px] leading-tight text-brand-700 hover:underline block"
          >
            {open.eventName}
          </Link>
          <span className="text-[12px] font-semibold text-[#8a8170] tabular-nums mt-0.5 block">
            {formatEventDate(open.eventDate)}
            {open.playersCount > 0 && (
              <>
                <span className="mx-1.5 text-[#c4baa6]">·</span>
                {open.playersCount} jogador
                {open.playersCount === 1 ? "" : "es"}
              </>
            )}
          </span>
        </div>

        {open.winner ? (
          <div className="rounded-lg bg-[#faf7ee] border border-[#efeae0] px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-[#8a8170] font-bold mb-0.5">
              Campeão
            </div>
            <div className="text-sm font-semibold text-ink truncate">
              {open.winner.playerName}
            </div>
            {open.winner.subarchetypeId ? (
              <Link
                to={`/subarchetypes/${open.winner.subarchetypeId}`}
                className="text-[12px] text-brand-700 hover:underline truncate block"
                title={open.winner.subarchetypeName ?? ""}
              >
                {open.winner.subarchetypeName}
                {open.winner.archetypeName && (
                  <span className="text-[#9a9182] font-normal">
                    {" "}
                    · {open.winner.archetypeName}
                  </span>
                )}
              </Link>
            ) : open.winner.deckName ? (
              <span className="text-[12px] text-[#6b6356] truncate block">
                {open.winner.deckName}
              </span>
            ) : (
              <span className="text-[12px] italic text-[#9a9182]">
                sem classificação
              </span>
            )}
          </div>
        ) : (
          <div className="rounded-lg bg-[#faf7ee] border border-[#efeae0] px-3 py-2 text-[12px] italic text-[#9a9182]">
            Ainda sem resultados registrados.
          </div>
        )}
      </div>
    </article>
  );
}
